import { type ReactElement, useMemo } from 'react';
import {
  type MetricThresholdEnabledState,
  MetricThresholdsSection,
} from '@/features/projects/components/MetricThresholdsSection';
import {
  parseAvgDurationSeconds,
  parseFrequencyPerDay,
  parseThresholdValue,
} from '@/features/projects/metricThresholdDrafts';
import { buildMetricThresholdPayload } from '@/features/projects/utils/buildMetricThresholdPayload';
import {
  defaultProjectMetricThresholds,
  mergeProjectThresholdGroup,
} from '@/features/projects/utils/projectMetricThresholds';
import { isMetricThresholdDisabled } from '@/shared/utils/metricsThresholds';
import type { useUpdateProject } from '@/features/projects';
import type { useMetricThresholdDrafts } from '@/features/projects/hooks/useMetricThresholdDrafts';
import type { MetricThresholdDrafts } from '@/features/projects/metricThresholdDrafts';
import type { SpacingToken } from '@/theme/tokens';
import type { MetricThresholdGroup, ProjectDto } from '@flowqa/shared';

type MutationErrorController = { clearError: () => void; setError: (err: unknown) => void };
type NotifyInput = { message: string; tone: 'green' | 'blue' | 'red' };

type MetricThresholdKey = 'suites' | 'executionRuns' | 'tests' | 'pipelines' | 'stages' | 'jobs';

type ProjectMetricThresholdSectionProps = {
  title: string;
  description: string;
  successMessage: string;
  thresholdKey: MetricThresholdKey;
  drafts: MetricThresholdDrafts;
  setDrafts: ReturnType<typeof useMetricThresholdDrafts>['setDrafts'];
  preferredUnitsRef: ReturnType<typeof useMetricThresholdDrafts>['preferredUnitsRef'];
  isValid: boolean;
  isPending: boolean;
  gridSpacing: SpacingToken;
  project: ProjectDto | null;
  updateProject: ReturnType<typeof useUpdateProject>;
  mutationError: MutationErrorController;
  notify: (input: NotifyInput) => void;
  resetDrafts: (thresholds: MetricThresholdGroup) => void;
  container?: 'panel' | 'none';
};
const defaultThresholdGroupByKey: Record<MetricThresholdKey, MetricThresholdGroup> =
  defaultProjectMetricThresholds;

const areVisibleThresholdDraftsDirty = (
  drafts: MetricThresholdDrafts,
  thresholds: MetricThresholdGroup,
): boolean => {
  const passRateTarget = parseThresholdValue(drafts.passRate.target);
  const passRateWarn = parseThresholdValue(drafts.passRate.warn);
  const frequencyTarget = parseFrequencyPerDay(
    drafts.frequency.targetValue,
    drafts.frequency.targetUnit,
  );
  const frequencyWarn = parseFrequencyPerDay(drafts.frequency.warnValue, drafts.frequency.warnUnit);
  const avgDurationTarget = parseAvgDurationSeconds(
    drafts.avgDuration.targetValue,
    drafts.avgDuration.targetUnit,
  );
  const avgDurationWarn = parseAvgDurationSeconds(
    drafts.avgDuration.warnValue,
    drafts.avgDuration.warnUnit,
  );

  if (
    passRateTarget === null ||
    passRateWarn === null ||
    frequencyTarget === null ||
    frequencyWarn === null ||
    avgDurationTarget === null ||
    avgDurationWarn === null
  ) {
    return true;
  }

  return (
    passRateTarget !== thresholds.passRate.target ||
    passRateWarn !== thresholds.passRate.warn ||
    frequencyTarget !== thresholds.frequency.target ||
    frequencyWarn !== thresholds.frequency.warn ||
    drafts.frequency.targetUnit !== (thresholds.frequency.targetUnit ?? 'days') ||
    drafts.frequency.warnUnit !== (thresholds.frequency.warnUnit ?? 'days') ||
    avgDurationTarget !== thresholds.avgDuration.target ||
    avgDurationWarn !== thresholds.avgDuration.warn ||
    drafts.avgDuration.targetUnit !== (thresholds.avgDuration.targetUnit ?? 'seconds') ||
    drafts.avgDuration.warnUnit !== (thresholds.avgDuration.warnUnit ?? 'seconds')
  );
};

export const ProjectMetricThresholdSection = ({
  title,
  description,
  successMessage,
  thresholdKey,
  drafts,
  setDrafts,
  preferredUnitsRef,
  isValid,
  isPending,
  gridSpacing,
  project,
  updateProject,
  mutationError,
  notify,
  resetDrafts,
  container = 'panel',
}: ProjectMetricThresholdSectionProps): ReactElement => {
  const deriveInitialEnabled = useMemo(
    () =>
      (thresholds: MetricThresholdGroup | null | undefined): MetricThresholdEnabledState => ({
        passRate: thresholds ? !isMetricThresholdDisabled(thresholds.passRate) : true,
        frequency: thresholds ? !isMetricThresholdDisabled(thresholds.frequency) : true,
        avgDuration: thresholds ? !isMetricThresholdDisabled(thresholds.avgDuration) : true,
      }),
    [],
  );
  const memoizedThresholds = useMemo(
    () =>
      project
        ? mergeProjectThresholdGroup(
            defaultThresholdGroupByKey[thresholdKey],
            project.metricThresholds[thresholdKey],
          )
        : defaultThresholdGroupByKey[thresholdKey],
    [project, thresholdKey],
  );
  const initialEnabled = useMemo(
    () => deriveInitialEnabled(memoizedThresholds),
    [deriveInitialEnabled, memoizedThresholds],
  );
  const visibleThresholdDraftsDirty = useMemo(
    () => areVisibleThresholdDraftsDirty(drafts, memoizedThresholds),
    [drafts, memoizedThresholds],
  );
  const baselineKey = useMemo(
    () =>
      JSON.stringify({
        thresholds: {
          passRate: memoizedThresholds.passRate,
          frequency: memoizedThresholds.frequency,
          avgDuration: memoizedThresholds.avgDuration,
        },
        enabled: initialEnabled,
      }),
    [initialEnabled, memoizedThresholds],
  );

  return (
    <MetricThresholdsSection
      title={title}
      description={description}
      passRateLabel={thresholdKey === 'pipelines' ? 'Success Rate' : 'Pass Rate'}
      frequencyLabel={(() => {
        if (thresholdKey === 'pipelines') {
          return 'Job Frequency';
        }
        if (thresholdKey === 'stages') {
          return 'Stage Frequency';
        }
        if (thresholdKey === 'jobs') {
          return 'Job Frequency';
        }
        return 'Run Frequency';
      })()}
      drafts={drafts}
      setDrafts={setDrafts}
      preferredUnitsRef={preferredUnitsRef}
      initialEnabled={initialEnabled}
      isDirty={visibleThresholdDraftsDirty}
      isValid={isValid}
      isPending={isPending}
      baselineKey={baselineKey}
      gridSpacing={gridSpacing}
      container={container}
      onCancel={() => {
        if (!project) return;
        resetDrafts(memoizedThresholds);
        mutationError.clearError();
      }}
      onSubmit={(enabled) => {
        if (!project) return;
        const payload = buildMetricThresholdPayload(drafts, enabled, memoizedThresholds);
        updateProject.mutate(
          { id: project.id, metricThresholds: { [thresholdKey]: payload } },
          {
            onSuccess: () => {
              resetDrafts(payload);
              mutationError.clearError();
              notify({ message: successMessage, tone: 'green' });
            },
            onError: (err) => {
              mutationError.setError(err);
            },
          },
        );
      }}
    />
  );
};
